import { UnionLogo, Visibility, VisibilityOff } from '@/components/icons'
import { identifyUser, setUserDetails } from '@/config/trackingFunctions'
import { ROUTE_CLAIM, ROUTE_DASHBOARD, ROUTE_FORGOT_PASSWORD, ROUTE_SIGNUP } from '@/constants/Routes'
import { COOKIE_SESSION } from '@/constants/Common'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { createSignInValidationSchema } from '@/utils/schema/SignInValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { capitalize, isEmpty } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { login, setRefresh, setStrongMfaSetupRequired, setToken } from '../authSlice'
import { updateUser } from '@/pages/user/userSlice'
import { TwoFactorChallenge } from '../components/TwoFactorChallenge'
import { ForcedPasswordReset } from '../components/ForcedPasswordReset'
import { findTwoFactorChallenge, hasStrongMfaSetup, requirementsFrom, SECOND_FACTOR_URN, SecondFactorKind } from '@/types'
import { Loading } from '@/components/loading'
import { Button } from '@/components/button'
import { eventTracker } from '@/config/trackingFunctions'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'

type FormValues = {
  email: string
  password: string
}

/** The partial-login state for an in-progress 2FA challenge, held only in this tab. */
type Challenge = { token: string; methods: SecondFactorKind[]; email: string }

export const POST_LOGIN_REDIRECT_KEY = 'dupr.postLoginRedirect'

export const Login = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Set when the API answers 428 TWO_FACTOR_CHALLENGE: the password was accepted but the session
  // is withheld until the emailed 6-digit code is entered. Held only in this tab.
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  // Set when the verify step halts with an in-place reset token (see TwoFactorChallenge).
  const [forcedReset, setForcedReset] = useState<{ resetToken: string } | null>(null)
  const dispatch = useAppDispatch()
  const { token, isLoading } = useAppSelector(authSelector)

  const [show, setShow] = useState(false)

  const validationSchema = createSignInValidationSchema(t)

  const [searchParams] = useSearchParams()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await dispatch(login(data)).unwrap()
      const userAge = moment().diff(result.result?.user?.birthdate, 'years')
      identifyUser(result.result?.user?.id)
      setUserDetails({
        Gender: capitalize(result.result?.user?.gender),
        Age: userAge,
        Geolocation: result.result?.user?.addresses?.[0]?.shortAddress,
        'Email ID': result.result?.user?.email,
      })
      eventTracker(TRACKING_TYPES.SIGN_IN, { 'Page Name': TRACKING_TYPES.LOGIN })
    } catch (err: any) {
      // The 428 carries the rich `requirements` array (URN-tagged). Gate on the presence of the
      // two-factor-challenge requirement rather than the legacy `requiredActions`/`challengeToken`
      // fields — a rejected password answers 401 and is otherwise body-identical, which is why the
      // bare status was never enough.
      const challengeReq = findTwoFactorChallenge(requirementsFrom(err))
      if (challengeReq) {
        // Password accepted, session withheld. Hold the challenge token in this tab and switch to
        // the code-entry screen; the code is what mints the session (INV-1).
        const redirect = searchParams.get('redirect')
        if (redirect) sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirect)
        // Both the token and the methods come FROM the rich requirement now (strong factors like
        // TOTP/passkey never appeared in the legacy top-level fields). Fall back to email if a body
        // somehow carried the challenge without methods.
        const methods: SecondFactorKind[] = challengeReq.methods ?? [{ type: SECOND_FACTOR_URN.EMAIL }]
        setChallenge({ token: challengeReq.challengeToken, methods, email: data.email })
        return
      }
      toast.error(err.message, { style: { marginBottom: '40px' } })
      eventTracker(TRACKING_TYPES.SIGN_IN_ERROR, { error: err.message })
    }
  })

  const onVerified = (data: any) => {
    const session = data?.result
    // The verify 200 carries the account's outstanding requirements. If it still owes strong-MFA
    // setup, raise the gate FIRST — before the session marker exists — so the app renders the
    // enrolment screen straight away and never mounts the dashboard (whose shell would fire
    // profile/status/credentials calls that 428 for a not-yet-fully-authenticated session).
    if (hasStrongMfaSetup(session?.requirements)) dispatch(setStrongMfaSetupRequired(true))
    // The first-party session rides HttpOnly cookies; the verify response carries no token. A 2xx
    // means the cookie is set, so record the marker (the token effect below navigates once redux
    // holds it) and store the user.
    dispatch(setToken(COOKIE_SESSION))
    dispatch(setRefresh(COOKIE_SESSION))
    if (session?.user) dispatch(updateUser(session.user))
  }

  const toggleShowPassword = () => setShow(!show)

  useEffect(() => {
    if (token) {
      const redirect = searchParams.get('redirect')

      return redirect !== null
        ? navigate(decodeURIComponent(redirect), { replace: true })
        : navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (token) {
    return <Loading />
  }

  if (forcedReset) {
    // Password + second factor are proven; the account must set a new password before its session
    // is minted. Completing the form finishes the login exactly like a verified code does.
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div className="flex flex-col justify-center items-center gap-y-12">
          <UnionLogo className="text-white" />
          <ForcedPasswordReset
            resetToken={forcedReset.resetToken}
            email={challenge?.email}
            onComplete={onVerified}
            onStartOver={() => {
              setForcedReset(null)
              setChallenge(null)
            }}
          />
        </div>
      </div>
    )
  }

  if (challenge) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div className="flex flex-col justify-center items-center gap-y-12">
          <UnionLogo className="text-white" />
          <TwoFactorChallenge
            challengeToken={challenge.token}
            methods={challenge.methods}
            email={challenge.email}
            onVerified={onVerified}
            onPasswordResetRequired={(resetToken) => setForcedReset({ resetToken })}
            onBack={() => setChallenge(null)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <form
        className="w-[300px] xs:w-[345px] flex flex-col gap-y-8"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <div className="flex flex-col justify-center items-center gap-y-12">
          <UnionLogo className="text-white" />
          <p className="text-xl font-bold text-white">{t('auth.heading')}</p>
        </div>

        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-2">
            <h5 className="text-white font-semibold text-sm">{t('common.email')}</h5>
            <input
              className={`w-full p-4 border rounded-full ${
                errors.email ? 'border-warn' : ''
              } text-sm text-navyBlue focus:outline-none`}
              type="text"
              autoFocus
              placeholder={t('common.emailPlaceHolder')}
              {...register('email')}
            />
            {errors?.email && <p className={`text-xs text-warn`}>{errors?.email?.message}</p>}
          </div>

          <div className="flex flex-col gap-y-2">
            <h5 className="text-white font-semibold text-sm">{t('common.password')}</h5>
            <div className="relative">
              <input
                className={`w-full p-4 border rounded-full ${
                  errors.password ? 'border-warn' : ''
                } text-sm text-navyBlue focus:outline-none`}
                placeholder={t('common.passwordPlaceHolder')}
                type={show ? 'text' : 'password'}
                {...register('password')}
              />
              <div className="h-full absolute right-0 top-0 flex items-center">
                <button
                  className="flex w-10 h-10 rounded-full items-center justify-center hover:bg-gray-100 mr-1"
                  onClick={toggleShowPassword}
                  type="button"
                >
                  {show ? (
                    <VisibilityOff className="w-6 h-6 text-navy-900" />
                  ) : (
                    <Visibility className="w-6 h-6 text-navy-900" />
                  )}
                </button>
              </div>
            </div>
            {errors?.password && <p className={`text-xs text-warn`}>{errors?.password?.message}</p>}
          </div>
        </div>

        <div className="flex flex-col gap-y-4 mt-4 items-center text-sm text-white font-semibold">
          <div className="flex flex-col items-center gap-y-2">
            <button
              className={`${
                isLoading ? 'bg-gray-400 text-gray-200' : 'bg-azul text-white'
              } w-60 rounded-full py-3 px-5  ${isEmpty(errors) ? 'hover:bg-primary' : 'opacity-50'}`}
              disabled={isLoading || !isEmpty(errors)}
              type="submit"
            >
              {t('auth.signIn')}
            </button>
            <Link to={ROUTE_SIGNUP}>
              <Button className="border-white border-solid border-2 text-sm text-white font-semibold  disabled:opacity-50 w-[240px] h-12 flex flex-col items-center justify-center">
                {t('auth.signUp')}
              </Button>
            </Link>
            <Link
              to={ROUTE_FORGOT_PASSWORD}
              className="w-60 rounded-full py-3 px-5 text-white text-center hover:text-opacity-70"
            >
              {t('auth.forgotPass')}
            </Link>
          </div>

          <div className="flex items-center justify-center">
            <p className="">{t('auth.claimQuestion')}</p>&nbsp;
            <Link to={ROUTE_CLAIM} className="text-white underline hover:text-opacity-70">
              {t('auth.claimAccount')}
            </Link>
          </div>

          {/* <div className="flex items-center justify-center">
            <p className="">{t('auth.noAccount')}</p>&nbsp;
            <Link to={ROUTE_SIGNUP} className="text-white underline hover:text-opacity-70">
              {t('auth.signUp')}
            </Link>
          </div> */}
        </div>
      </form>
    </div>
  )
}
